import { Box, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import { palette } from "../styles/palette";
import { useThemeMode } from "./contexts/themeModeContext";

interface ThemeModeToggleProps {
  /** Optional caption shown above the switch. */
  label?: string;
  /** Compact single icon-button form, suited to nav bars. */
  compact?: boolean;
}

/**
 * Dark / light appearance switch, backed by the app-wide theme mode context.
 * Drop it anywhere inside <ThemeModeProvider> (e.g. the profile screen or
 * the top navbar with `compact`).
 */
export const ThemeModeToggle: React.FC<ThemeModeToggleProps> = ({
  label = "Appearance",
  compact = false,
}) => {
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === "dark";

  if (compact) {
    return (
      <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
        <IconButton
          onClick={toggleMode}
          sx={{ color: palette.accent }}
          aria-label="Toggle dark mode"
        >
          {isDark ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
      }}
    >
      {label && (
        <Typography sx={{ color: palette.textSecondary, fontSize: "0.85rem" }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          px: "10px",
          py: "2px",
          borderRadius: "9999px",
          border: `1px solid ${palette.border}`,
          background: palette.bgSurface,
        }}
      >
        <LightModeIcon
          fontSize="small"
          sx={{ color: isDark ? palette.textMuted : palette.accent }}
        />
        <Switch
          checked={isDark}
          onChange={toggleMode}
          color="primary"
          inputProps={{ "aria-label": "Toggle dark mode" }}
        />
        <DarkModeIcon
          fontSize="small"
          sx={{ color: isDark ? palette.accent : palette.textMuted }}
        />
      </Box>
    </Box>
  );
};

export default ThemeModeToggle;
